<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { NODE_TYPES } from './constants';

const store = useStore();
const getters = useStoreGetters();
const router = useRouter();
const { t } = useI18n();

const isCreating = ref(false);
const records = computed(() => getters['flows/getFlows'].value);
const uiFlags = computed(() => getters['flows/getUIFlags'].value);
const accountId = computed(() => getters.getCurrentAccountId.value);

onMounted(() => store.dispatch('flows/get'));

const openFlow = id =>
  router.push({
    name: 'flows_edit',
    params: { accountId: accountId.value, flowId: id },
  });

const createFlow = async () => {
  isCreating.value = true;
  try {
    const flow = await store.dispatch('flows/create', {
      name: t('FLOWS.DEFAULT_NAME'),
      active: false,
      blocks: [
        {
          id: 'trigger',
          type: 'trigger',
          presentation: { x: 0, y: 0 },
          options: NODE_TYPES.trigger.defaults(),
        },
      ],
    });
    openFlow(flow.id);
  } catch (error) {
    useAlert(t('FLOWS.API.CREATE_ERROR'));
  } finally {
    isCreating.value = false;
  }
};

const cloneFlow = async id => {
  try {
    await store.dispatch('flows/clone', id);
    useAlert(t('FLOWS.API.CLONE_SUCCESS'));
  } catch (error) {
    useAlert(t('FLOWS.API.CLONE_ERROR'));
  }
};

const deleteFlow = async flow => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('FLOWS.CONFIRM_DELETE', { name: flow.name }))) return;
  try {
    await store.dispatch('flows/delete', flow.id);
    useAlert(t('FLOWS.API.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('FLOWS.API.DELETE_ERROR'));
  }
};

const toggleActive = async flow => {
  try {
    await store.dispatch('flows/update', { id: flow.id, active: !flow.active });
  } catch (error) {
    useAlert(t('FLOWS.API.TOGGLE_ERROR'));
  }
};

const stepCount = flow =>
  (flow.blocks || []).filter(block => block.type !== 'trigger').length;
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('FLOWS.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('FLOWS.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('FLOWS.HEADER')"
        :description="$t('FLOWS.DESCRIPTION')"
      >
        <template #actions>
          <Button
            :label="$t('FLOWS.CREATE')"
            icon="i-lucide-plus"
            :is-loading="isCreating"
            @click="createFlow"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-3">
        <div
          v-for="flow in records"
          :key="flow.id"
          class="group flex items-center gap-4 px-5 py-4 rounded-2xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container hover:outline-n-strong hover:shadow-md cursor-pointer transition-all duration-200"
          @click="openFlow(flow.id)"
        >
          <span
            class="flex items-center justify-center w-10 h-10 rounded-xl shrink-0 transition-transform group-hover:scale-105"
            :class="
              flow.active
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-alpha-2 text-n-slate-10'
            "
          >
            <span class="i-lucide-workflow w-5 h-5" />
          </span>

          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-n-slate-12 truncate">
              {{ flow.name }}
            </p>
            <p class="text-xs text-n-slate-10 truncate mt-0.5">
              {{ $t('FLOWS.STEP_COUNT', { n: stepCount(flow) }) }}
              <template v-if="flow.description">
                · {{ flow.description }}
              </template>
            </p>
          </div>

          <span
            class="text-xs font-medium px-2.5 py-1 rounded-lg shrink-0"
            :class="
              flow.active
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-alpha-2 text-n-slate-11'
            "
          >
            {{ flow.active ? $t('FLOWS.ACTIVE') : $t('FLOWS.PAUSED') }}
          </span>

          <div
            class="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
            @click.stop
          >
            <button
              class="p-2 rounded-xl text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              :title="
                flow.active
                  ? $t('FLOWS.ACTIONS.PAUSE')
                  : $t('FLOWS.ACTIONS.ACTIVATE')
              "
              @click="toggleActive(flow)"
            >
              <span
                :class="flow.active ? 'i-lucide-pause' : 'i-lucide-play'"
                class="w-4 h-4"
              />
            </button>
            <button
              class="p-2 rounded-xl text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              :title="$t('FLOWS.ACTIONS.CLONE')"
              @click="cloneFlow(flow.id)"
            >
              <span class="i-lucide-copy w-4 h-4" />
            </button>
            <button
              class="p-2 rounded-xl text-n-slate-11 hover:bg-n-ruby-3 hover:text-n-ruby-11 transition-colors"
              :title="$t('FLOWS.ACTIONS.DELETE')"
              @click="deleteFlow(flow)"
            >
              <span class="i-lucide-trash-2 w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
